import { UIColor, UIText, UITypography } from '@perpetua1/oasis-react';
import cn from 'classnames';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useMemo, useState } from 'react';
import { customDataLayerPush, obscureString } from 'src/utils/analytics';
import { FORM_TYPE, LABEL, DL_EVENT } from 'src/utils/analytics/constants';
import { Hubspot } from '../../../constants/destinations';
import ThankYou from '../../../images/business-intelligence/thank-you.svg';
import { sendToHubspot } from '../../../services/hubspot';
import { ANALYTICS_FIELDS } from '../../../utils/analytics/hiddenFields';
import { CHILIPIPER, chiliPiperSubmitHSForm } from '../../../utils/chilipiper';
import { getCountriesList } from '../../../utils/countries';
import { validateEmail } from '../../../utils/email';
import AdSpendInput from '../../AdSpendInput';
import Button, { ButtonSize, ButtonVariant } from '../../Button';
import CountryInput from '../../CountryInput';
import FormErrorMessage from '../../FormErrorMessage';
import Loading from '../../Loading';
import TermsAndConditions from '../../TermsAndConditions';

interface PrismFormProps {
  buttonText?: string;
  successText?: string;
  formId: string;
  formType: string;
  termsAndConditions: string;
  marketplaceAvailable?: string;
}

const PrismForm: React.FC<PrismFormProps> = props => {
  const {
    buttonText = 'Schedule a Demo',
    formId,
    formType,
    termsAndConditions,
    marketplaceAvailable,
  } = props;

  const { t } = useTranslation();

  const [userInfo, setUserInfo] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    phone: string;
  }>({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
  });
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [inputError, setInputError] = React.useState<boolean>(false);
  const [adSpend, setAdSpend] = useState<{
    title?: string;
    hsID?: string;
  }>({ title: '', hsID: '' });
  const [countries, setCountries] = useState<
    {
      name: string;
      code: string;
    }[]
  >([]);
  const [location, setLocation] = useState<{
    name?: string;
    code?: string;
  }>({ name: '', code: '' });

  const { language } = useI18next();

  useMemo(() => {
    const fetchCountries = async () => {
      const countriesList = await getCountriesList();
      setCountries(countriesList);
    };
    fetchCountries();
  }, []);

  const isValidForm = () => {
    return (
      userInfo.firstName !== '' &&
      userInfo.lastName !== '' &&
      validateEmail(userInfo.email) &&
      userInfo.company !== '' &&
      userInfo.phone !== '' &&
      adSpend.title !== '' &&
      location.name !== ''
    );
  };
  const clearForm = () => {
    setUserInfo({
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      phone: '',
    });
    setAdSpend({ title: '', hsID: '' });
    setLocation({ name: '', code: '' });
  };

  const submitToHubSpot = async () => {
    const portalId = Hubspot.portalId;
    const formGuid = formId;

    const postData = {
      portalId,
      formGuid,
      fields: [
        {
          name: 'firstname',
          value: userInfo.firstName,
        },
        {
          name: 'lastname',
          value: userInfo.lastName,
        },
        {
          name: 'email',
          value: userInfo.email,
        },
        {
          name: 'company',
          value: userInfo.company,
        },
        {
          name: 'phone',
          value: userInfo.phone,
        },
        {
          name: 'perpetua_ad_spend_tier',
          value: adSpend?.hsID,
        },
        {
          name: 'country',
          value: location ? location.name : '',
        },
        ...ANALYTICS_FIELDS(),
      ],
    };

    setInputError(false);
    const { status } = await sendToHubspot(
      postData
    );
    if (status === 200) {
      // Prism
      customDataLayerPush(DL_EVENT.formSubmit, {
        formLabel: LABEL.Prism,
        formAdSpend: adSpend?.hsID,
        leadUserData: {
          email: obscureString(userInfo.email),
          phone_number: obscureString(userInfo.phone),
          address: {
            first_name: obscureString(userInfo.firstName),
            last_name: obscureString(userInfo.lastName),
            country: obscureString(location?.name as string),
            postal_code: '12345',
          },
        }
      });

      chiliPiperSubmitHSForm(
        {
          firstname: userInfo.firstName,
          lastname: userInfo.lastName,
          email: userInfo.email,
          phone: userInfo.phone,
          company: userInfo.company,
          perpetua_ad_spend_tier: adSpend.hsID,
          where_are_you_located_country_code_en: location ? location.name : '',
          country: location ? location.name : ''
        },
        CHILIPIPER.routerPrism,
        language
      );
      clearForm();
      setLoading(false);
      setSubmitted(true);
    }
  };

  const buttonOnClick = () => {
    if (isValidForm()) {
      setLoading(true);
      submitToHubSpot();
    } else {
      setInputError(true);
    }
  };

  const sharedInputClasses =
    'newsletter-signup-input border border-gray-3 rounded-lg focus:border-indigo-1 focus:outline-none focus:ring-4 focus:ring-indigo-3-light py-2 px-4 w-full text-base mt-2';

  const sharedLabelClasses = 'text-base font-semibold';

  return (
    <div
      aria-hidden={true}
      className="max-w-full md:max-w-[600px] mx-auto mt-10"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={sharedLabelClasses}>{t('First Name')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && userInfo.firstName.length <= 0 && 'input-error'
            )}
            placeholder={t('First Name')}
            value={userInfo.firstName}
            onChange={e =>
              setUserInfo({ ...userInfo, firstName: e.target.value })
            }
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>{t('Last Name')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && userInfo.lastName.length <= 0 && 'input-error'
            )}
            placeholder={t('Last Name')}
            value={userInfo.lastName}
            onChange={e =>
              setUserInfo({ ...userInfo, lastName: e.target.value })
            }
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>{t('Work Email')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && !validateEmail(userInfo.email) && 'input-error'
            )}
            placeholder={t('Work Email')}
            value={userInfo.email}
            onChange={e => setUserInfo({ ...userInfo, email: e.target.value })}
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>{t('Company')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && userInfo.company.length <= 0 && 'input-error'
            )}
            placeholder={t('Company')}
            value={userInfo.company}
            onChange={e =>
              setUserInfo({ ...userInfo, company: e.target.value })
            }
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>{t('Phone')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && userInfo.phone.length <= 0 && 'input-error'
            )}
            placeholder={t('Phone')}
            value={userInfo.phone}
            onChange={e => setUserInfo({ ...userInfo, phone: e.target.value })}
          />
        </div>
        <div>
          <label className={`${sharedLabelClasses} mb-2 block`}>
            {t('Where are you located?')}
          </label>
          <CountryInput
            countries={countries}
            location={location}
            setLocation={setLocation}
            inputError={inputError}
          />
        </div>
        <div>
          <label className={`${sharedLabelClasses} mb-2 block`}>
            {t('What is your current monthly ad spend?')}
          </label>
          <AdSpendInput
            inputError={inputError}
            adSpend={adSpend}
            setAdSpend={setAdSpend}
          />
        </div>
      </div>

      {inputError && <FormErrorMessage />}

      <Button
        className="mx-auto py-4 px-8 my-10"
        variant={ButtonVariant.primary}
        text={t(buttonText)}
        size={ButtonSize.large}
        onClick={e => {
          buttonOnClick();
        }}
      />

      {formType === FORM_TYPE.PRISM && (
        <UIText
          className="pt-5 text-left !font-normal"
          typography={UITypography.body}
          color={UIColor.grayLight2}
        >
          {marketplaceAvailable}
        </UIText>
      )}

      <TermsAndConditions content={termsAndConditions} />

      {loading && (
        <div className="transition duration-200 absolute top-0 w-full h-full left-0 p-8 bg-white flex items-center justify-center rounded-xl">
          <Loading />
        </div>
      )}
      {submitted && (
        <div className="absolute w-full h-full left-0 top-0 p-8 bg-white flex items-center justify-center rounded-xl z-50">
          <div className="mx-auto flex flex-col gap-8 items-center">
            <ThankYou />
            <h3 className="text-5xl font-semibold max-w-xs text-center leading-10 align-center">
              {t('Thank You!')}
            </h3>
            <Button
              className="mx-auto py-2 px-22"
              variant={ButtonVariant.primary}
              text={t('Resubmit')}
              size={ButtonSize.large}
              onClick={e => {
                setSubmitted(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PrismForm;
